import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getOffice } from "../../Services/Office";
import type { MemberSearchOption } from "./memberSearchQuery";
import {
  buildMemberSearchOfficeLocationOptions,
  type MemberSearchOfficeRecord,
} from "./memberSearchOfficeOptions";

const CACHE_KEY = "memberSearchOfficeLocations-v4";
const CACHE_TTL_MS = 30 * 60 * 1000;

type MemberFirmRef = {
  id: string;
  name: string;
  legacyId?: string | number;
  shortId?: string | number;
  jurisdiction?: unknown;
};

type CachedOfficeLocations = {
  expiresAt: number;
  memberFirmCount: number;
  offices: MemberSearchOfficeRecord[];
};

const readCachedOffices = (
  memberFirmCount: number,
): MemberSearchOfficeRecord[] | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CachedOfficeLocations;
    if (
      !parsed?.offices?.length ||
      parsed.expiresAt <= Date.now() ||
      parsed.memberFirmCount !== memberFirmCount
    ) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed.offices;
  } catch {
    return null;
  }
};

const writeCachedOffices = (
  offices: MemberSearchOfficeRecord[],
  memberFirmCount: number,
): void => {
  try {
    const payload: CachedOfficeLocations = {
      offices,
      memberFirmCount,
      expiresAt: Date.now() + CACHE_TTL_MS,
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Cache is best-effort.
  }
};

export const useMemberSearchOfficeLocations = (
  memberFirms: MemberFirmRef[],
  firmsLoaded: boolean,
  selectedFirmId?: string,
) => {
  const [allOffices, setAllOffices] = useState<MemberSearchOfficeRecord[]>([]);
  const [isLoadingOfficeLocations, setIsLoadingOfficeLocations] = useState(false);
  const loadPromiseRef = useRef<Promise<void> | null>(null);

  const memberFirmRefs = useMemo(
    () =>
      memberFirms.map((firm) => ({
        id: firm.id,
        jurisdiction: firm.jurisdiction,
      })),
    [memberFirms],
  );

  const loadOfficeLocations = useCallback(async (): Promise<void> => {
    if (!firmsLoaded) {
      return;
    }

    if (!memberFirms.length) {
      setAllOffices([]);
      return;
    }

    if (loadPromiseRef.current) {
      return loadPromiseRef.current;
    }

    const cached = readCachedOffices(memberFirms.length);
    if (cached) {
      setAllOffices(cached);
      return;
    }

    const loadPromise = (async () => {
      setIsLoadingOfficeLocations(true);
      try {
        const offices = await getOffice();
        const records = (offices ?? []) as MemberSearchOfficeRecord[];
        writeCachedOffices(records, memberFirms.length);
        setAllOffices(records);
      } catch (error) {
        console.error("Failed to load member search office locations:", error);
        setAllOffices([]);
      } finally {
        setIsLoadingOfficeLocations(false);
        loadPromiseRef.current = null;
      }
    })();

    loadPromiseRef.current = loadPromise;
    return loadPromise;
  }, [firmsLoaded, memberFirms.length]);

  useEffect(() => {
    void loadOfficeLocations();
  }, [loadOfficeLocations]);

  const officeLocations: MemberSearchOption[] = useMemo(
    () =>
      buildMemberSearchOfficeLocationOptions(
        allOffices,
        memberFirmRefs,
        selectedFirmId,
      ),
    [allOffices, memberFirmRefs, selectedFirmId],
  );

  return {
    officeLocations,
    isLoadingOfficeLocations,
    loadOfficeLocations,
  };
};
